import { all, call, put, select, take, takeLatest } from 'redux-saga/effects';
import { eventChannel, EventChannel } from 'redux-saga';
import { PayloadAction } from '@reduxjs/toolkit';

import { t } from '$translation/helper';
import {
  getApiUrl,
  getOnlyPayPopUpShown,
  getStorageItem,
  isNative,
  scrollToBottomWithCheck,
  statReachGoal,
  trackPurchase,
  transliterate,
} from '$utils';
import { batchActions } from '$store';
import { mainActions } from '$store/main';
import {
  imActions,
  imAsPeerIdSelector,
  imCallSelector,
  imHistorySelector,
} from '$store/im';
import router from '$navigation/router';
import { CHAT, IM, RESOLVE } from '$shared/constants/pages';
import { MessageModel } from '$store/models';
import { showNotify } from '$services/notifications';
import { blacklistActions } from '$store/blacklist';
import { toastActions } from '$store/toast';
import { realtimeActions } from '$store/realtime/index';
import { balanceActions } from '$store/balance';
import { monetizationActions } from '$store/monetization';
import { goBack, goTo } from '$navigation/helper';
import { postsActions, postsSelector } from '$store/posts';
import { payActions, paySelector } from '$store/pay';
import { notifyActions } from '$store/notify';
import { balancePayActions, balancePaySelector } from '$store/balancePay';
import { updateAllBalancesWorker } from '$store/balance/sagas';
import { CallStatus } from '$store/im/interface';
import { getCardWorker } from '$store/pay/sagas';
import { BalancePayAction } from '$store/balancePay/interface';
import { liveActions, liveSelector } from '$store/live';
import { LiveState, LiveStatus } from '$store/live/interface';
import { pushesActions } from '$store/pushes';
import { PushType } from '$store/pushes/interface';
import { authSelector } from '$store/auth';
import { trialLinksActions } from '$store/trialLinks';
import { notesSelector } from '$store/notes';
import { wsLog } from '$utils/wsDebug';

let Channel: EventChannel<any> | null = null;

async function initWebsocket(token: string) {
  Channel = eventChannel((emitter) => {
    let socket: WebSocket | null = null;
    let reconnectTimer: any = 0;
    let currentConnId = 0;
    let forceClosed = false;
    let lastEventId = 0;
    let reconnectCount = 0;
    let lastMessageAt = Date.now();

    const healthTimer = setInterval(() => {
      const readyState = socket ? socket.readyState : -1;
      const gapMs = Date.now() - lastMessageAt;
      const online = typeof navigator !== 'undefined' ? navigator.onLine : null;
      const visibility =
        typeof document !== 'undefined' ? document.visibilityState : null;

      wsLog('health', {
        readyState,
        gapMs,
        reconnects: reconnectCount,
        online,
        visibility,
      });
    }, 10000);

    function connect() {
      clearTimeout(reconnectTimer);

      const connId = ++currentConnId;

      if (socket) {
        try {
          socket.close();
          socket = null;
        } catch (e) {}
      }

      const url = new URL('/ws', getApiUrl());
      url.protocol = url.protocol.replace('http', 'ws');

      url.searchParams.set('access_token', token);
      url.searchParams.set('last_event_id', String(lastEventId));

      wsLog('connect', {
        connId,
        lastEventId,
        online: typeof navigator !== 'undefined' ? navigator.onLine : null,
        visibility: typeof document !== 'undefined' ? document.visibilityState : null,
      });

      socket = new WebSocket(url.toString());
      socket.onopen = () => {
        wsLog('open', { connId, afterReconnects: reconnectCount });
        reconnectCount = 0;
      };

      socket.onclose = (err) => {
        wsLog('close', {
          connId,
          code: err.code,
          reason: err.reason,
          wasClean: err.wasClean,
          readyState: socket ? socket.readyState : -1,
        });
        if (connId === currentConnId) {
          connectionDidClose();
        }
      };
      socket.onmessage = (e) => {
        const now = Date.now();
        const gapMs = now - lastMessageAt;
        lastMessageAt = now;

        const messages = e.data.split('\n');
        for (let i = 0; i < messages.length; i++) {
          let event;
          try {
            event = JSON.parse(messages[i]);
          } catch (err) {
            wsLog('parse_error', { message: err.message });
            continue;
          }

          wsLog('event', { id: event.id, type: event.type, gapMs });
          lastEventId = event.id;
          emitter(event);
        }
      };

      socket.onerror = () => {
        wsLog('error', { connId, readyState: socket ? socket.readyState : -1 });
        if (connId === currentConnId) {
          connectionDidClose();
        }
      };
    }

    function connectionDidClose() {
      if (forceClosed) {
        return;
      }

      reconnectCount += 1;
      wsLog('reconnect_scheduled', { inMs: 3000, reconnects: reconnectCount });

      clearTimeout(reconnectTimer);
      reconnectTimer = setTimeout(connect, 3000);
    }

    connect();

    return () => {
      clearInterval(healthTimer);
      clearTimeout(reconnectTimer);
      forceClosed = true;
      wsLog('teardown', {});
      socket?.close();
      socket = null;
    };
  });

  return Channel;
}

// async function initWebsocket(token: string) {
//   Channel = eventChannel((emitter) => {
//     const es = new EventSource(`https://api.ton.place/es?access_token=${token}`);
//     es.onopen = () => {
//       console.log('es connected');
//     };
//
//     es.onmessage = (e) => {
//       console.log('es onmessage', e.data);
//       try {
//         const parsed = JSON.parse(e.data);
//         emitter(parsed);
//       } catch (e) {
//         console.log('cant parse event', e.message);
//       }
//     };
//
//     es.onerror = (e) => {
//       console.log('es onerror', e);
//     };
//
//     return () => {
//       es.close();
//     };
//   });
//
//   return Channel;
// }

function* handleEvent(event: any) {
  try {
    const { type, body } = event;

    // @ts-ignore
    switch (type) {
      case 'connect': {
        console.log('ws connected');
        break;
      }

      case 'batch': {
        for (const item of body) {
          try {
            yield call(handleEvent, JSON.parse(item));
          } catch (e) {}
        }
        break;
      }

      case 'message': {
        const lang = getStorageItem('lang');
        const asPeerId = yield select(imAsPeerIdSelector);
        const { currentUserId } = yield select(authSelector);
        const selfId = asPeerId || currentUserId;
        const isOwnContext =
          body.message.fromId === selfId || body.message.toId === selfId;

        if (isOwnContext) {
          yield put(
            batchActions(
              mainActions.setUser(body.user),
              imActions.setMessage({
                peerId: body.dialog.peerId,
                messageId: body.message.isInbox ? body.message.id : body.randomId,
                message: body.message,
              }),
              imActions.setDialogs({
                dialogs: [body.dialog],
                messages: {},
              }),
              imActions.resetTyping(body.message.fromId),
            ),
          );
        }
        const routerState = router.getState();
        if (body.message.isInbox) {
          const isInChat =
            routerState.name === CHAT &&
            +routerState.params.peerId === body.dialog.peerId;
          if (isOwnContext && isInChat) {
            yield call(scrollToBottomWithCheck, 'chat_history');
            if (!document.hidden) {
              yield put(
                imActions.readHistory({
                  peerId: body.dialog.peerId,
                  ...(asPeerId ? { asPeerId } : {}),
                }),
              );
            }
          } else if (isOwnContext) {
            const { notes } = yield select(notesSelector);

            const userNote = notes.find((item) => item.userId === body.user.id);
            const name = userNote?.name
              ? userNote?.name
              : lang === 'ru'
              ? body.user.firstName
              : transliterate(body.user.firstName);

            yield put(
              batchActions(
                pushesActions.push({
                  type: PushType.MESSAGE,
                  userId: body.user.id,
                  objectId: body.user.id,
                  title: t(
                    body.user.sex
                      ? 'push_message_title_female'
                      : 'push_message_title_male',
                    { name },
                  ),
                  text: body.message.text,
                }),
              ),
            );
          }

          if ((!isNative(2) && routerState.name !== IM && !isInChat) || document.hidden) {
            let text = `${body.user.firstName} ${body.user.lastName}: ${
              body.message.text ?? ''
            }`;

            if (body.message.attachments && body.message.attachments.length > 0) {
              if (body.message.attachments.length > 1) {
                text += ' Вложения';
              } else if (body.message.attachments[0].type === 'photo') {
                text += ' Фото';
              }
            }

            yield call(
              showNotify,
              'Новое сообщение',
              text,
              () => {
                if (
                  routerState.name === CHAT &&
                  +routerState.params.peerId === body.dialog.peerId
                ) {
                  // already in chat
                } else {
                  router.navigate(CHAT, { peerId: body.dialog.peerId });
                }
              },
              { image: body.user.photo },
            );
          }
        }
        break;
      }

      case 'read_history': {
        const history = yield select(imHistorySelector, body.peerId);
        if (history) {
          const updatedMessages: { [index: number]: MessageModel } = [];
          for (const msgId in history) {
            const message = history[msgId];
            if (!message.isInbox && message.isUnread) {
              updatedMessages[message.id] = { ...message, isUnread: false };
            }
          }

          if (Object.keys(updatedMessages).length > 0) {
            yield put(
              imActions.setHistory({
                peerId: body.peerId,
                history: updatedMessages,
              }),
            );
          }
        }

        break;
      }

      case 'dialogs_counter': {
        yield put(mainActions.setNewDialogs(body.count));
        break;
      }

      case 'ban_from_me': {
        yield put(
          blacklistActions.setBanFromMe({
            userId: body.userId,
            isBanned: true,
          }),
        );
        break;
      }

      case 'ban_to_me': {
        yield put(
          blacklistActions.setBanToMe({
            userId: body.userId,
            isBanned: true,
          }),
        );
        const { liveStatus, stream } = (yield select(liveSelector)) as LiveState;
        if (liveStatus === LiveStatus.ONLINE && stream?.streamerId === body.userId) {
          if (stream?.id) {
            yield put(liveActions.removeStream(stream.id));
          }
          yield put(liveActions.banned());
        }
        break;
      }

      case 'unban_from_me': {
        yield put(
          blacklistActions.setBanFromMe({
            userId: body.userId,
            isBanned: false,
          }),
        );
        break;
      }

      case 'unban_to_me': {
        yield put(
          blacklistActions.setBanToMe({
            userId: body.userId,
            isBanned: false,
          }),
        );
        break;
      }

      case 'has_notify': {
        yield put(mainActions.setHasNotify(body));
        break;
      }

      case 'delete_dialog': {
        yield put(imActions.removeDialog(body.peerId));
        break;
      }

      case 'delete_message': {
        yield put(
          batchActions(
            imActions.setDialogs({
              dialogs: [body.dialog],
              messages: {},
            }),
            imActions.removeMessage({
              id: body.id,
              peerId: body.peerId,
            }),
          ),
        );
        break;
      }

      case 'trial_activated': {
        // SUBSCRIPTION_ADULT activation (trial links не используют WebSocket)
        yield put(
          batchActions(
            mainActions.setDecryptKey({
              id: -1,
              decryptKey: body.decryptKey,
            }),
            toastActions.setToastSuccess(t('subscription_active_toast')),
            payActions.setInvoiceToken(null),
          ),
        );
        yield call(trackPurchase, 1, BalancePayAction.SUBSCRIPTION_ADULT, 'trial');
        yield call(statReachGoal, 'subscription_adult_bought');
        break;
      }
      case 'card_attached': {
        console.log('card_attached');
        yield put(trialLinksActions.attachCardConfirm());
        break;
      }
      case 'erotic_activated': {
        yield put(
          batchActions(
            mainActions.setDecryptKey({
              id: -1,
              decryptKey: body.decryptKey,
            }),
            toastActions.setToastSuccess(t('subscription_active_toast')),
            payActions.setInvoiceToken(null),
          ),
        );
        yield call(trackPurchase, 1, BalancePayAction.SUBSCRIPTION_ADULT, 'qr_bonus');
        yield call(statReachGoal, 'subscription_adult_bought');
        break;
      }
      case 'subscription_update': {
        if (body.has) {
          yield put(toastActions.setToastSuccess(t('subscription_active_toast')));
        } else {
          yield put(toastActions.setToastSuccess(t('subscription_inactive_toast')));
        }

        if (body.category > 0 || body.authorId > 0) {
          yield put(
            batchActions(
              mainActions.setDecryptKey({
                id: body.authorId > 0 ? body.authorId : -body.category,
                decryptKey: body.decryptKey,
              }),
            ),
          );
        } else {
          yield put(
            batchActions(
              mainActions.setHasSubscription(body.has),
              mainActions.setDecryptKeyLegacy(body.decryptKey),
            ),
          );
        }
        break;
      }

      case 'top_up': {
        yield put(
          toastActions.setToastSuccess(
            t('balance_top_upped', {
              amount: body.amount,
            }),
          ),
        );
        yield put(
          batchActions(
            balanceActions.setBalance(body.balance),
            mainActions.setBalance(body.balance),
          ),
        );
        break;
      }

      case 'bought_paid_message': {
        yield put(toastActions.setToastSuccess(t('bought_paid_message')));
        yield put(
          imActions.markMessageAsBought({
            peerId: body.peerId,
            messageId: body.messageId,
          }),
        );
        break;
      }

      case 'verification_status': {
        yield put(monetizationActions.setMonetizationStatus(body.status));
        break;
      }

      case 'pay_error': {
        const { randomInvoiceId, invoiceToken } = yield select(paySelector);
        // const route = yield call(router.getState);

        if (body.code) {
          yield put(payActions.setErrorCode(body.code));
        }

        if (body.code === 'session_cancelled_by_partner') {
          yield yield put(mainActions.deleteDecryptKey(-1));
          return yield yield put(
            toastActions.setToastFail(t('toast_subscription_canceled')),
          );
        }

        if (randomInvoiceId === body.randomId) {
          if (!invoiceToken) {
            // Оплата с прикрепленной карты
            yield put(toastActions.setToastFail(t('toast_payment_failed')));
          } else {
            // Оплата с новой карты (форма мерчанта)
            yield put(payActions.setIsFailed(true));
          }
        } else if (!body.randomId) {
          yield put(toastActions.setToastFail(t('toast_payment_failed')));
        }

        yield put(payActions.setIsWaitingForPay(false));

        yield getCardWorker();
        // if (body.objectId === 0) {
        //   if (route.name === SUBSCRIPTION_FIAT) {
        //     yield call(goBack);
        //   }
        // } else if (body.objectId < 0) {
        //   if (body.objectId === ContentPriorityType.ADULT) {
        //     if (route.name === SUBSCRIPTION_ADULT) {
        //       yield call(goBack);
        //     }
        //   } else {
        //     if (route.name === SUBSCRIPTION_MEDIA) {
        //       yield call(goBack);
        //     }
        //   }
        // } else {
        //   if (
        //     route.name === SUBSCRIPTION_AUTHOR &&
        //     route.params.authorId === body.objectId
        //   ) {
        //     yield call(goBack);
        //   }
        // }
        // yield put(mainActions.setCard(null));
        break;
      }

      case 'paid_message': {
        const history = yield select(imHistorySelector, body.peerId);
        const message = history[body.messageId];
        if (!message) {
          return;
        }

        yield put(
          batchActions(
            imActions.setMessage({
              peerId: body.peerId,
              messageId: body.messageId,
              message: {
                ...message,
                isHidden: false,
                decryptKey: body.decryptKey,
              },
            }),
            toastActions.setToastSuccess(t('bought_message')),
          ),
        );
        break;
      }

      case 'post_tips_update': {
        const { posts } = yield select(postsSelector);

        const post = posts[body.postId];
        if (post) {
          yield put(
            postsActions.setPost({
              id: body.postId,
              updated: {
                tips: +body.tips,
              },
            }),
          );
          yield put(toastActions.hideToast());
        }
        break;
      }

      case 'cryptopay_invoice_confirmation': {
        const { invoiceId } = yield select(paySelector);
        if (invoiceId === +body.invoiceId) {
          yield put(payActions.cancel());
          goBack();
        }
        break;
      }

      case 'typing': {
        yield put(
          imActions.setTyping({
            peerId: body.peerId,
            typing: true,
          }),
        );
        break;
      }

      case 'telegram_connected': {
        yield put(notifyActions.setTelegramConnected(true));
        break;
      }

      case 'eur_balance_top_up': {
        console.log('eur_balance_top_up');
        const { randomInvoiceId } = yield select(paySelector);
        yield put(
          batchActions(
            payActions.setPayConfirmUrl(''),
            mainActions.setEurBalance(body.balance),
            balanceActions.setEurBalance(body.balance),
            balancePayActions.setActive(false),
            toastActions.hideToast(),
          ),
        );

        const actions: any[] = [];
        if (!body.randomId) {
          actions.push(
            balanceActions.setTopUpActive({
              topUpActive: false,
            }),
          );
        } else if (body.randomId === randomInvoiceId) {
          actions.push(payActions.setInvoiceToken(null));
          const { amount } = yield select(balancePaySelector);
          if (amount && body.balance >= amount) {
            actions.push(
              balanceActions.setTopUpActive({
                topUpActive: false,
              }),
            );
            actions.push(balancePayActions.reopen());
            actions.push(
              balanceActions.setTopUpTonActive({
                topUpActive: false,
              }),
            );
          } else {
            actions.push(
              toastActions.setToastSuccess(
                t('eur_balance_top_upped', {
                  balance: body.balance,
                }),
              ),
            );
          }
        }
        yield put(batchActions(...actions));

        yield updateAllBalancesWorker();
        yield getCardWorker();
        // ToDo: display success icon

        if (getOnlyPayPopUpShown()) {
          yield put(mainActions.setPopup(null));
        }
        break;
      }

      case 'telegram_status': {
        const history = yield select(imHistorySelector, body.peerId);
        const message = history[body.messageId];
        if (!message) {
          return;
        }

        yield put(
          imActions.setMessage({
            peerId: body.peerId,
            messageId: body.messageId,
            message: {
              ...message,
              telegramStatus: body.status,
            },
          }),
        );
        break;
      }
      case 'call': {
        yield put(
          imActions.incomingCall({
            peerId: body.userId,
            remoteDescription: JSON.parse(atob(body.description)),
            callId: body.callId,
            iceServers: body.iceServers,
          }),
        );
        break;
      }
      case 'call_accept': {
        yield put(imActions.setCallRemoteDescription(JSON.parse(atob(body.description))));
        if (body.iceServers?.length) {
          yield put(
            imActions.setCallIceConfig({
              callId: body.callId || null,
              iceServers: body.iceServers,
            }),
          );
        }
        break;
      }
      case 'call_reject': {
        yield put(imActions.hangup());
        yield put(toastActions.setToastFail(t('call_reject')));
        break;
      }
      case 'call_cancel': {
        yield put(imActions.hangup());
        break;
      }
      case 'call_complete': {
        const { status } = yield select(imCallSelector);
        if (status === CallStatus.RINGING) {
          yield put(imActions.hangup());
        }
        break;
      }

      case 'pay_action_require': {
        console.log('pay_action_require');
        const { isWaitingForPay } = yield select(paySelector);
        if (isWaitingForPay) {
          yield put(payActions.setPayConfirmUrl(body.url));
          yield put(toastActions.hideToast());
          yield put(payActions.setIsWaitingForPay(false));
        }
        break;
      }

      case 'live_started': {
        const lang = getStorageItem('lang');

        const { currentUserId } = yield select(authSelector);
        const { notes } = yield select(notesSelector);

        const userNote = notes.find((item) => item.userId === body.user.id);
        const name = userNote?.name
          ? userNote?.name
          : lang === 'ru'
          ? body.user.firstName
          : transliterate(body.user.firstName);

        if (body.user.id === currentUserId) return;

        yield put(
          batchActions(
            mainActions.setUser(body.user),
            pushesActions.push({
              type: PushType.LIVE,
              userId: body.user.id,
              objectId: body.streamId,
              title: t(
                body.user.sex
                  ? 'push_live_started_title_female'
                  : 'push_live_started_title_male',
                { name },
              ),
              text: t('push_live_started_text'),
            }),
          ),
        );
        break;
      }

      default:
        console.log('Unknown event', body.event);
    }
  } catch (e) {}
}

function* listenWorker(action: PayloadAction<string>) {
  const chan = yield call(initWebsocket, action.payload);

  // FOR emit realtime events
  // yield delay(3000);
  // yield call(handleEvent, { type: 'card_attached', body: {}, id: 1685011556666396621 });

  try {
    while (true) {
      const body = yield take(chan);
      yield call(handleEvent, body);
    }
  } finally {
    Channel = null;
  }
}

function* unlistenWorker() {
  if (Channel) {
    yield call(Channel.close);
    Channel = null;
  }
}

export function* realtimeSaga() {
  yield all([
    takeLatest(realtimeActions.listen, listenWorker),
    takeLatest(realtimeActions.unListen, unlistenWorker),
  ]);
}
